import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Field } from '@/components/synapse/Field';
import { Icon } from '@/components/synapse/Icon';
import { Modal } from '@/components/synapse/Modal';
import { InitialsAvatar } from '@/components/synapse/Avatar';
import { useAuth } from '@/hooks/useAuth';
import { useClinicMembers, useRemoveMember, useUpdateMember } from '@/hooks/usePeople';
import {
  useCreateInvitation,
  useListInvitations,
  useRevokeInvitation,
} from '@/hooks/useClinics';
import { useClinicRoles, useSetMemberRoles } from '@/hooks/useRoles';
import { MultiSelectPills } from '@/components/synapse/MultiSelectPills';
import { ROLES_MANAGE } from '@/lib/permissions';
import type { ClinicMember, ClinicRole, Invitation } from '@/types/api';
import { ApiError } from '@/lib/api';
import { translateApiError } from '@/lib/apiError';
import { cn } from '@/lib/utils';

export function PeoplePage() {
  const { t } = useTranslation();
  const { user, permissions } = useAuth();
  const { data: members = [], isLoading, error } = useClinicMembers();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ClinicMember | null>(null);
  const [removeTarget, setRemoveTarget] = useState<ClinicMember | null>(null);

  // This page is routed on `members.read`; reading and sending invitations are
  // their own permissions. Asked before rendering AND before fetching, so a
  // member who may see the staff list but not the invitations doesn't collect a
  // guaranteed 403 on load.
  const canReadInvitations = permissions.has('invitations.read');
  const canInvite = permissions.has('invitations.create');

  const { data: roles = [] } = useClinicRoles();

  // Who can still manage access. This replaced an "admin count": with custom
  // roles there is no single role that means administrator, so the question has
  // to be asked of the permission itself.
  const managerRoleIds = useMemo(
    () => new Set(roles.filter((r) => r.permissions.includes(ROLES_MANAGE)).map((r) => r.id)),
    [roles],
  );
  const holdsRolesManage = (m: ClinicMember) => m.roles.some((r) => managerRoleIds.has(r.id));
  const managerCount = useMemo(
    () => members.filter(holdsRolesManage).length,
    [members, managerRoleIds],
  );

  return (
    <div className="mx-auto w-full max-w-[1180px]">
      <header className="mb-5 flex flex-wrap items-end justify-between gap-3 sm:mb-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-[-0.02em] sm:text-[26px]">{t('people.title')}</h1>
          <p className="mt-1 text-[13.5px] text-muted">{t('people.subtitle')}</p>
        </div>
        {canInvite && (
          <Button onClick={() => setInviteOpen(true)}>
            <Icon name="plus" size={16} /> {t('people.inviteMember')}
          </Button>
        )}
      </header>

      <Card className="overflow-hidden p-0">
        {isLoading ? (
          <div className="px-5 py-10 text-center text-[13px] text-muted">{t('people.loadingMembers')}</div>
        ) : error ? (
          <div className="px-5 py-10 text-center text-[13px] text-destructive">
            {t('people.couldNotLoad')}
          </div>
        ) : members.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-6 py-12 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-paper-2 text-muted">
              <Icon name="users" size={22} />
            </div>
            <div className="text-[13.5px] font-semibold tracking-[-0.01em]">{t('people.noMembersYet')}</div>
            {/* Only to someone who can act on it. Telling a member without
                `invitations.create` to invite their first teammate is advice
                they cannot take — there is no button on this page for them. */}
            {canInvite && (
              <div className="max-w-[260px] text-[12.5px] text-muted">
                {t('people.addFirstTeammate')}
              </div>
            )}
          </div>
        ) : (
          <ul className="divide-y divide-line">
            {members.map((m) => {
              const isSelf = m.id === user?.id;
              const isLastManager = holdsRolesManage(m) && managerCount <= 1;
              return (
                <li key={m.id} className="flex items-center gap-3 px-4 py-3.5 sm:px-5">
                  <InitialsAvatar name={m.name} />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="text-[14px] font-semibold tracking-[-0.01em]">
                        {m.name}
                      </span>
                      {isSelf && (
                        <span className="inline-flex h-[18px] items-center rounded-full bg-info-bg px-1.5 text-[10.5px] font-medium text-info">
                          {t('people.youBadge')}
                        </span>
                      )}
                    </div>
                    <div className="mt-0.5 truncate text-[12.5px] text-muted">{m.email}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <RolePills roles={m.roles} managerRoleIds={managerRoleIds} />
                    <span className="hidden font-mono text-[11.5px] tabular-nums text-muted sm:inline">
                      {formatJoined(m.joinedAt, t)}
                    </span>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        aria-label={`${t('common.edit')} ${m.name}`}
                        onClick={() => setEditTarget(m)}
                        className="flex h-8 w-8 items-center justify-center rounded-md border border-line bg-paper text-muted hover:text-ink hover:bg-paper-2"
                      >
                        <Icon name="edit" size={14} />
                      </button>
                      <button
                        type="button"
                        aria-label={`${t('common.remove')} ${m.name}`}
                        disabled={isSelf || isLastManager}
                        title={
                          isSelf
                            ? t('people.cantRemoveSelf')
                            : isLastManager
                            ? t('people.mustHaveManager')
                            : undefined
                        }
                        onClick={() => setRemoveTarget(m)}
                        className="flex h-8 w-8 items-center justify-center rounded-md border border-line bg-paper text-muted hover:text-destructive hover:bg-paper-2 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:text-muted"
                      >
                        <Icon name="trash" size={13} />
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        <CardContent className="border-t border-line bg-paper-2 px-4 py-2.5 text-[11.5px] text-muted sm:px-5">
          {t('people.membersFooter')}
        </CardContent>
      </Card>

      {canReadInvitations && <PendingInvitationsCard managerRoleIds={managerRoleIds} />}

      {inviteOpen && <InviteMemberDialog roles={roles} onClose={() => setInviteOpen(false)} />}
      {editTarget && (
        <EditMemberDialog
          member={editTarget}
          roles={roles}
          isLastManager={holdsRolesManage(editTarget) && managerCount <= 1}
          managerRoleIds={managerRoleIds}
          isSelf={editTarget.id === user?.id}
          onClose={() => setEditTarget(null)}
        />
      )}
      {removeTarget && (
        <RemoveMemberDialog member={removeTarget} onClose={() => setRemoveTarget(null)} />
      )}
    </div>
  );
}

function formatJoined(iso: string, t: (key: string, opts?: Record<string, unknown>) => string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return t('people.joinedOn', { date: d.toISOString().slice(0, 10) });
}

function formatInvited(iso: string, t: (key: string, opts?: Record<string, unknown>) => string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return t('people.invitedOn', { date: d.toISOString().slice(0, 10) });
}

/**
 * One badge per role held. Roles carrying `roles.manage` are toned `info` so
 * "who can change access here" is answerable at a glance — the one distinction
 * worth colour now that role names are clinic-authored.
 *
 * A member with no roles renders an explicit "no access" badge rather than
 * nothing: an empty cell reads as a loading state, and this is a real and
 * reachable condition (their last role was deleted).
 */
function RolePills({
  roles,
  managerRoleIds,
}: {
  roles: ClinicMember['roles'];
  managerRoleIds: ReadonlySet<string>;
}) {
  const { t } = useTranslation();
  if (roles.length === 0) {
    return (
      <Badge tone="outline" size="sm">
        {t('people.noRoles')}
      </Badge>
    );
  }
  return (
    <span className="flex flex-wrap items-center gap-1">
      {roles.map((r) => (
        <Badge key={r.id} tone={managerRoleIds.has(r.id) ? 'info' : 'outline'} size="sm">
          {r.name}
        </Badge>
      ))}
    </span>
  );
}

/** Role picker shared by the create and edit dialogs. */
/**
 * `lockedRoleIds` are shown as selected and cannot be deselected — everything
 * else stays interactive. That distinction is the whole point: a member can be
 * pinned to one role without freezing the rest of their assignment.
 */
function RoleMultiSelect({
  roles,
  value,
  onChange,
  lockedRoleIds = [],
}: {
  roles: ClinicRole[];
  value: string[];
  onChange: (next: string[]) => void;
  lockedRoleIds?: string[];
}) {
  const { t } = useTranslation();
  const locked = new Set(lockedRoleIds);
  return (
    <MultiSelectPills
      value={value}
      onChange={(next) => {
        // Re-add anything locked that this change tried to drop, rather than
        // discarding the whole change — the user's other edits still apply.
        const kept = [...next, ...lockedRoleIds.filter((id) => !next.includes(id))];
        onChange(kept);
      }}
      options={roles.map((r) => ({
        value: r.id,
        label: locked.has(r.id) ? `${r.name} ${t('people.roleLockedSuffix')}` : r.name,
      }))}
      ariaLabel={t('people.roles')}
    />
  );
}

interface InviteMemberDialogProps {
  roles: ClinicRole[];
  onClose: () => void;
}

/**
 * An email address and the roles they arrive with — nothing else.
 *
 * This used to create the account outright, with a password the admin chose. It
 * cannot: an account is global, so a person may already have one (the create
 * would 409) and the password would be their credential at every OTHER clinic
 * they work at. The invitee accepts for themselves, from the clinic picker.
 */
function InviteMemberDialog({ roles, onClose }: InviteMemberDialogProps) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [roleIds, setRoleIds] = useState<string[]>([]);
  // Set once the invitation lands but the email does not — see below.
  const [undelivered, setUndelivered] = useState<string | null>(null);
  const invite = useCreateInvitation();

  // At least one role: a member with none can do nothing at all, so inviting
  // someone that way is almost certainly a slip. The API enforces it too.
  const valid = useMemo(
    () => /^\S+@\S+\.\S+$/.test(email.trim()) && roleIds.length > 0,
    [email, roleIds],
  );

  const submit = async () => {
    if (!valid) return;
    try {
      const created = await invite.mutateAsync({ email: email.trim(), roleIds });
      // The invitation is committed either way; only the announcement failed.
      // Closing on a silent `emailDelivered: false` would leave the admin
      // believing somebody was contacted who was not — so the dialog stays open
      // and says so, and re-submitting is not the fix (it would conflict).
      if (!created.emailDelivered) {
        setUndelivered(created.email);
        return;
      }
      onClose();
    } catch {
      // Inline below.
    }
  };

  if (undelivered) {
    return (
      <Modal
        title={t('people.inviteMember')}
        onClose={onClose}
        footer={
          <Button full onClick={onClose}>
            {t('common.close')}
          </Button>
        }
      >
        <div className="rounded-md bg-warn-bg/40 px-3 py-2 text-[12.5px] text-warn">
          {t('people.inviteSentEmailFailed', { email: undelivered })}
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      title={t('people.inviteMember')}
      description={t('people.inviteMemberDesc')}
      onClose={onClose}
      isPending={invite.isPending}
      footer={
        <>
          <Button variant="secondary" full onClick={onClose} disabled={invite.isPending}>
            {t('common.cancel')}
          </Button>
          <Button full onClick={submit} disabled={!valid || invite.isPending}>
            {invite.isPending ? t('people.invitingBtn') : t('people.inviteMember')}
          </Button>
        </>
      }
    >
      <Field label={t('common.email')}>
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t('people.emailPlaceholder')}
          autoComplete="off"
          autoFocus
        />
      </Field>
      <Field label={t('people.roles')} hint={t('people.inviteRolesHint')}>
        <RoleMultiSelect roles={roles} value={roleIds} onChange={setRoleIds} />
      </Field>
      {invite.isError && (
        <div className="rounded-md bg-destructive-bg/40 px-3 py-2 text-[12.5px] text-destructive">
          {humanisePeopleError(invite.error, t)}
        </div>
      )}
    </Modal>
  );
}

/**
 * Invitations sent but not yet accepted.
 *
 * Rendered as its own card rather than mixed into the member list: an invitee is
 * not a member, holds nothing, and can be revoked but not edited — showing the
 * two together would invite exactly the confusion the roles column is there to
 * dispel.
 */
function PendingInvitationsCard({
  managerRoleIds,
}: {
  managerRoleIds: ReadonlySet<string>;
}) {
  const { t } = useTranslation();
  const { permissions } = useAuth();
  const { data: invitations = [], isLoading, error } = useListInvitations();
  const [revokeTarget, setRevokeTarget] = useState<Invitation | null>(null);
  const canRevoke = permissions.has('invitations.revoke');

  return (
    <>
      <Card className="mt-5 overflow-hidden p-0 sm:mt-6">
        <div className="border-b border-line px-4 py-3 sm:px-5">
          <h2 className="text-[14px] font-semibold tracking-[-0.01em]">
            {t('people.pendingInvitations')}
          </h2>
        </div>
        {isLoading ? (
          <div className="px-5 py-8 text-center text-[13px] text-muted">
            {t('people.pendingInvitationsLoading')}
          </div>
        ) : error ? (
          <div className="px-5 py-8 text-center text-[13px] text-destructive">
            {t('people.pendingInvitationsError')}
          </div>
        ) : invitations.length === 0 ? (
          <div className="px-5 py-8 text-center text-[13px] text-muted">
            {t('people.pendingInvitationsEmpty')}
          </div>
        ) : (
          <ul className="divide-y divide-line">
            {invitations.map((inv) => (
              <li key={inv.id} className="flex items-center gap-3 px-4 py-3.5 sm:px-5">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-paper-2 text-muted">
                  <Icon name="mail" size={16} />
                </div>
                <div className="min-w-0 flex-1 truncate text-[14px] font-medium tracking-[-0.01em]">
                  {inv.email}
                </div>
                <div className="flex items-center gap-3">
                  <InvitationRolePills roles={inv.roles} managerRoleIds={managerRoleIds} />
                  <span className="hidden font-mono text-[11.5px] tabular-nums text-muted sm:inline">
                    {formatInvited(inv.createdAt, t)}
                  </span>
                  {canRevoke && (
                    <button
                      type="button"
                      aria-label={`${t('people.revokeInvite')} ${inv.email}`}
                      onClick={() => setRevokeTarget(inv)}
                      className="flex h-8 w-8 items-center justify-center rounded-md border border-line bg-paper text-muted hover:bg-paper-2 hover:text-destructive"
                    >
                      <Icon name="trash" size={13} />
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
        <CardContent className="border-t border-line bg-paper-2 px-4 py-2.5 text-[11.5px] text-muted sm:px-5">
          {t('people.pendingInvitationsFooter')}
        </CardContent>
      </Card>

      {revokeTarget && (
        <RevokeInvitationDialog
          invitation={revokeTarget}
          onClose={() => setRevokeTarget(null)}
        />
      )}
    </>
  );
}

/**
 * One badge per role the invitation grants.
 *
 * A role deleted since the invite was sent renders as such, because it is the
 * whole story: the invitation cannot be claimed at all any more. In practice
 * deleting a role revokes the invitations naming it, so this is the narrow
 * window between the two — but it is a state the API can return, and an
 * unexplained blank badge is worse than a plain label.
 */
function InvitationRolePills({
  roles,
  managerRoleIds,
}: {
  roles: Invitation['roles'];
  managerRoleIds: ReadonlySet<string>;
}) {
  const { t } = useTranslation();
  return (
    <span className="flex flex-wrap items-center gap-1">
      {roles.map((r) => (
        <Badge
          key={r.roleId ?? r.roleName}
          tone={r.roleId !== null && managerRoleIds.has(r.roleId) ? 'info' : 'outline'}
          size="sm"
        >
          {r.roleId === null ? `${r.roleName} · ${t('people.inviteRoleDeleted')}` : r.roleName}
        </Badge>
      ))}
    </span>
  );
}

function RevokeInvitationDialog({
  invitation,
  onClose,
}: {
  invitation: Invitation;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const revoke = useRevokeInvitation();

  const submit = async () => {
    try {
      await revoke.mutateAsync(invitation.id);
      onClose();
    } catch {
      // Inline below.
    }
  };

  return (
    <Modal
      title={t('people.revokeInviteTitle', { email: invitation.email })}
      description={t('people.revokeInviteDesc')}
      onClose={onClose}
      isPending={revoke.isPending}
      footer={
        <>
          <Button variant="secondary" full onClick={onClose} disabled={revoke.isPending}>
            {t('common.cancel')}
          </Button>
          <Button full variant="danger" onClick={submit} disabled={revoke.isPending}>
            {revoke.isPending ? t('people.revokingInvite') : t('people.revokeInvite')}
          </Button>
        </>
      }
    >
      {revoke.isError && (
        <div className="rounded-md bg-destructive-bg/40 px-3 py-2 text-[12.5px] text-destructive">
          {humanisePeopleError(revoke.error, t)}
        </div>
      )}
    </Modal>
  );
}

interface EditMemberDialogProps {
  member: ClinicMember;
  roles: ClinicRole[];
  isLastManager: boolean;
  managerRoleIds: ReadonlySet<string>;
  isSelf: boolean;
  onClose: () => void;
}

function EditMemberDialog({
  member,
  roles,
  isLastManager,
  managerRoleIds,
  isSelf,
  onClose,
}: EditMemberDialogProps) {
  const { t } = useTranslation();
  const [name, setName] = useState(member.name);
  const [roleIds, setRoleIds] = useState<string[]>(member.roles.map((r) => r.id));
  const update = useUpdateMember();
  const setRoles = useSetMemberRoles();

  const originalRoleIds = useMemo(
    () => member.roles.map((r) => r.id).sort().join(','),
    [member.roles],
  );
  const rolesChanged = [...roleIds].sort().join(',') !== originalRoleIds;
  const profileChanged = name.trim() !== member.name;

  const dirty = profileChanged || rolesChanged;
  const valid = name.trim().length > 0 && roleIds.length > 0;

  // Two requests, because profile and roles are two different permissions on
  // two different endpoints. Not atomic: if the second fails the rename has
  // already landed. That is acceptable — a stale name is harmless — but it
  // makes rendering the roles mutation's error non-optional, which is what the
  // combined `isError` below does.
  const submit = async () => {
    if (!valid || !dirty) return;
    try {
      if (profileChanged) {
        await update.mutateAsync({ id: member.id, patch: { name: name.trim() } });
      }
      if (rolesChanged) {
        await setRoles.mutateAsync({ id: member.id, payload: { roleIds } });
      }
      onClose();
    } catch {
      // Inline below.
    }
  };

  const isPending = update.isPending || setRoles.isPending;
  // This member is the clinic's ONLY access manager, so they must keep at least
  // one role that grants it — the API refuses any change that would leave
  // nobody able to manage access.
  //
  // Only that. Disabling the whole picker (which is what this used to do) also
  // blocked adding a role, and blocked swapping one managing role for another —
  // both of which the server allows. In a single-admin clinic, the common case,
  // it meant that admin could never touch their own roles at all.
  const mustKeepManagingRole = isLastManager;
  const wouldOrphanAccess = (next: string[]) =>
    mustKeepManagingRole && !next.some((id) => managerRoleIds.has(id));
  const handleRoleChange = (next: string[]) => {
    if (wouldOrphanAccess(next)) return;
    setRoleIds(next);
  };

  return (
    <Modal
      title={t('people.editMember', { name: member.name })}
      description={member.email}
      onClose={onClose}
      isPending={isPending}
      footer={
        <>
          <Button variant="secondary" full onClick={onClose} disabled={isPending}>
            {t('common.cancel')}
          </Button>
          <Button full onClick={submit} disabled={!valid || !dirty || isPending}>
            {isPending ? t('people.saving') : t('people.saveChanges')}
          </Button>
        </>
      }
    >
      <Field label={t('common.name')}>
        <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
      </Field>
      <Field
        label={t('people.roles')}
        hint={
          mustKeepManagingRole
            ? t('people.rolesLockedHint')
            : isSelf
            ? t('people.selfRoleHint')
            : t('people.rolesHint')
        }
      >
        <RoleMultiSelect
          roles={roles}
          value={roleIds}
          onChange={handleRoleChange}
          lockedRoleIds={
            mustKeepManagingRole
              ? roleIds.filter(
                  (id) =>
                    managerRoleIds.has(id) &&
                    // The last one standing: deselecting it is what the rule
                    // forbids. A second managing role leaves this one free.
                    roleIds.filter((other) => managerRoleIds.has(other)).length === 1,
                )
              : []
          }
        />
      </Field>

      {/* No password field. An account is global — one person, several clinics —
          so a password an admin sets here would be that person's credential
          everywhere else they work. They reset their own from the sign-in page. */}

      {/* Both mutations, because the submit runs two requests and either can
          fail on its own. Showing only the profile one would let a refused role
          change — the lockout guard, an escalation refusal — vanish silently. */}
      {(update.isError || setRoles.isError) && (
        <div className="rounded-md bg-destructive-bg/40 px-3 py-2 text-[12.5px] text-destructive">
          {humanisePeopleError(update.error ?? setRoles.error, t)}
        </div>
      )}
    </Modal>
  );
}

interface RemoveMemberDialogProps {
  member: ClinicMember;
  onClose: () => void;
}

function RemoveMemberDialog({ member, onClose }: RemoveMemberDialogProps) {
  const { t } = useTranslation();
  const remove = useRemoveMember();

  const submit = async () => {
    try {
      await remove.mutateAsync(member.id);
      onClose();
    } catch {
      // Inline below.
    }
  };

  return (
    <Modal
      title={t('people.removeMemberTitle', { name: member.name })}
      description={t('people.removeMemberDesc')}
      onClose={onClose}
      isPending={remove.isPending}
      footer={
        <>
          <Button variant="secondary" full onClick={onClose} disabled={remove.isPending}>
            {t('common.cancel')}
          </Button>
          <Button full variant="danger" onClick={submit} disabled={remove.isPending}>
            {remove.isPending ? t('people.removing') : t('people.removeMemberBtn')}
          </Button>
        </>
      }
    >
      <div className="rounded-md border border-line bg-paper-2 px-3 py-2 text-[12.5px]">
        <div className="font-semibold text-ink">{member.name}</div>
        <div className="mt-0.5 text-muted">{member.email}</div>
      </div>
      {remove.isError && (
        <div className="rounded-md bg-destructive-bg/40 px-3 py-2 text-[12.5px] text-destructive">
          {humanisePeopleError(remove.error, t)}
        </div>
      )}
    </Modal>
  );
}

function humanisePeopleError(err: unknown, t: (key: string) => string): string {
  if (err instanceof ApiError && err.code === 'VALIDATION_ERROR') {
    return t('people.errFieldsInvalid');
  }
  return translateApiError(err, { fallback: t('people.errGeneric') });
}
